<template>
  <div>
    <print-preview-app-detail-header :show-toggle="false"
      :show-print="false"
      :show-back="!isNewBrowserTab"
      @appDetailHeaderButtonClicked="onHeaderButtonClicked" />
    <div id="pdf-container"
      name="pdf-container"
      :style="`height: calc(100vh - ${toolbarHeight}px); overflow: hidden;`">
      <object id="pdfDoc"
        class="pdf-document"
        :data="pdfUrl"
        type="application/pdf">
        <embed :src="pdfUrl"
          class="pdf-document"
          type="application/pdf">
      </object>
      <!-- <iframe id="pdfDoc"
        src=""
        type="application/pdf"
        style="width: 100%; height: 100%;" /> -->
    </div>
    <print-preview-export-modal v-if="isExportModalActive"
      :active.sync="isExportModalActive"
      @exportReport="exportReport"
      @close="closeExportModal" />
    <vue-email-modal v-if="isEmailModalActive"
      :active.sync="isEmailModalActive"
      icon-name="mdi-email-open"
      icon-type="primary"
      @send="sendEmail"
      @discard="discardEmail"
      @attach="attachMedia"
      @show-attach="showAttachImage"
      @attach-docs="attachDocs"
      @attach-files="onAttachFiles"
      @done-attach="doneAttach"
      :enable-send="enableSend && !isImageAttachActive && !isDocAttachActive"
      :enable-attach="enableAttach"
      :toggle-attach-button="isImageAttachActive || isDocAttachActive">
      <div slot="text-content">
        <vue-email v-if="isEmailModalActive && !isImageAttachActive && !isDocAttachActive"
          ref="emailer"
          v-model="emailer"
          :report-bytes="reportBytes"
          :report-name="`${emailer.reportName}.${reportExtension}`"
          :report-content-type="reportContentType"
          :quote-image-attachments="selectedImages"
          :quote-attachments="selectedDocuments"
          @new-email="newEmailInput"
          @sent="sent"
          @validation-changed="handleValidationChanged"
          :asset-id="emailer.assetId" />

        <image-select v-if="emailer && isImageAttachActive"
          v-show="isImageAttachActive"
          v-model="selectedImages"
          :image-select-with-details="true"
          :quote-id="emailer.assetId"
          :quote-ids="quoteIds"
          @input="onImagesSelected" />
        <attachment-select v-if="emailer && isDocAttachActive"
          v-show="isDocAttachActive"
          v-model="selectedDocuments"
          :quote-id="emailer.assetId"
          @input="onDocumentsSelected" />
      </div>
    </vue-email-modal>

    <!-- Validation Warning Outside Modal -->
    <div v-if="isEmailModalActive && emailValidation.isAttachmentSizeExceeded"
      class="notification is-danger is-light mt-2">
      <span class="icon">
        <i class="mdi mdi-alert" />
      </span>
      <span>{{ emailValidation.errors.join(' ') }}</span>
    </div>
  </div>
</template>

<script>
import PrintPreviewAppDetailHeader from './PrintPreviewAppDetailHeader'
import PrintPreviewService from './PrintPreviewService'
import PrintPreviewExportModal from './PrintPreviewExportModal'
import { ReportFormatTypes } from '@/enums'
import ImageSelect from '@/components/ImageSelect'
import { VueEmail, VueEmailModal } from '@/components/VueEmail'
import { Emailer } from '@/classes'
import StoreMixin from './storeMixin'
import _cloneDeep from 'lodash/cloneDeep'
import mimeTypes from 'mime-types'
import Guid from '@/components/Guid'
import StoreUtil from '@/store/utils'
// import _isEmpty from 'lodash/isEmpty'
import AttachmentSelect from '@/views/quote/components/AttachmentSelect.vue'
import { mapGetters } from 'vuex'

// Temporary const for copyright message
const divider = '=============================================================='
const copyright =
  'All accompanying materials are the exclusive property of [COMPANY].  All [COMPANY] rights are reserved.  These Materials must not be reproduced, adapted, modified, published, or communicated to any person in any form, without express prior written consent of [COMPANY].  Any unauthorised use of these Materials will be vigorously pursued and may result in legal action, including a claim for damages or an account for loss of profits.'

export default {
  name: 'PrintPreview',
  components: {
    PrintPreviewAppDetailHeader,
    PrintPreviewExportModal,
    VueEmail,
    VueEmailModal,
    ImageSelect,
    AttachmentSelect
  },
  mixins: [StoreMixin],
  props: {
    reportName: {
      type: String,
      default: '',
      required: true
    },
    quoteIds: {
      type: Array,
      default: () => {
        return []
      }
    }
  },
  data() {
    return {
      pdfBase64: '',
      pdfUrl: '',
      reportBytes: null,
      reportContentType: '',
      isExportModalActive: false,
      toolbarHeight: 0,
      isEmailModalActive: false,
      toEmails: [],
      ccEmails: [],
      bccEmails: [],
      emailer: null,
      isEnteringNewEmail: false,
      isNewBrowserTab: this.$route.query.isNewTab || false,
      isImageAttachActive: false,
      isDocAttachActive: false,
      selectedImageIds: [],
      selectedDocuments: [],
      // images: [],
      inputTagList: [],
      selectedImages: [],
      parameterId: this.$route.query.parameterId,
      parameters: null,
      cacheKey: '',
      allowedImageExtensions: ['.jpg', '.jpeg', '.png'],
      allowedDocumentExtensions: ['.pdf'],
      emailValidation: {
        canSend: true,
        isAttachmentSizeExceeded: false,
        totalSize: 0,
        errors: []
      }
    }
  },
  computed: {
    ...mapGetters({
      company: 'company/company'
    }),
    companyEnabledAutoCC() {
      return this.company.setting ? this.company.setting.enabledAutoCC : false
    },
    isAllowAutoCC() {
      return this.companyEnabledAutoCC && this.$userInfo.email && !this.emailer.ccEmails.includes(this.$userInfo.email)
    },
    reportExtension() {
      return mimeTypes.extension(this.reportContentType)
    },
    enableSend() {
      return !this.isEnteringNewEmail && this.emailValidation.canSend
    },
    enableAttach() {
      return (
        this.reportName === 'rptQuoteDollarHour' ||
        this.reportName === 'rptQuoteDollarOnly' ||
        this.reportName === 'rptQuoteDollarHourAdditionals' ||
        this.reportName === 'rptQuoteDollarOnlyAdditionals' ||
        this.reportName === 'rptPartsEnquiry' ||
        this.reportName.startsWith('rptInvoice')
      )
    },
    allAttachmentNames() {
      // Combine attachment names from attachments and customAttachments (avoid duplicates)
      const names = []
      if (this.emailer && Array.isArray(this.emailer.attachments)) {
        for (const att of this.emailer.attachments) {
          if (typeof att === 'string') {
            names.push(att)
          } else if (att && att.fileName) {
            names.push(att.fileName)
          }
        }
      }
      if (this.emailer && Array.isArray(this.emailer.customAttachments)) {
        for (const att of this.emailer.customAttachments) {
          if (att && att.fileName && !names.includes(att.fileName)) {
            names.push(att.fileName)
          }
        }
      }
      return names
    },
    totalAttachmentSize() {
      let totalSize = 0

      // Report file size (estimate based on reportBytes)
      if (this.reportBytes) {
        totalSize += this.reportBytes.byteLength || this.reportBytes.length || 0
      }

      // Standard attachments
      if (this.emailer && Array.isArray(this.emailer.attachments)) {
        for (const att of this.emailer.attachments) {
          if (typeof att === 'object' && att.fileSize) {
            totalSize += att.fileSize
          }
        }
      }

      // Custom file attachments
      if (this.emailer && Array.isArray(this.emailer.customAttachments)) {
        for (const att of this.emailer.customAttachments) {
          if (att && att.fileBytes) {
            totalSize += att.fileBytes.byteLength
          }
        }
      }

      // Quote document attachments
      if (this.emailer && Array.isArray(this.emailer.quoteAttachments)) {
        for (const att of this.emailer.quoteAttachments) {
          if (att && (att.originalFileSize || att.fileSize)) {
            totalSize += att.originalFileSize || att.fileSize || 0
          }
        }
      }

      // Selected images from image selector
      if (this.selectedImages && Array.isArray(this.selectedImages)) {
        for (const img of this.selectedImages) {
          if (img && (img.originalFileSize || img.fileSize)) {
            totalSize += img.originalFileSize || img.fileSize || 0
          }
        }
      }

      return totalSize
    },
    isAttachmentSizeExceeded() {
      const maxSizeBytes = 25 * 1024 * 1024 // 25MB in bytes
      return this.totalAttachmentSize > maxSizeBytes
    },
    combinedAttachments() {
      const attachmentObjects = []
      const processedNames = []

      // Report file
      if (this.reportBytes && this.emailer && this.emailer.reportName) {
        const reportFileName = `${this.emailer.reportName}.${this.reportExtension}`
        if (!processedNames.includes(reportFileName)) {
          processedNames.push(reportFileName)
          attachmentObjects.push({
            fileName: reportFileName,
            fileSize: this.reportBytes.length || this.reportBytes.byteLength || 0
          })
        }
      }

      // Standard attachments
      if (this.emailer && Array.isArray(this.emailer.attachments)) {
        for (const att of this.emailer.attachments) {
          if (typeof att === 'string') {
            if (!processedNames.includes(att)) {
              processedNames.push(att)
              attachmentObjects.push({ fileName: att, fileSize: 0 })
            }
          } else if (att && att.fileName && !processedNames.includes(att.fileName)) {
            processedNames.push(att.fileName)
            attachmentObjects.push({
              fileName: att.fileName,
              fileSize: att.fileSize || 0
            })
          }
        }
      }

      // Custom file attachments
      if (this.emailer && Array.isArray(this.emailer.customAttachments)) {
        for (const att of this.emailer.customAttachments) {
          if (att && att.fileName && !processedNames.includes(att.fileName)) {
            processedNames.push(att.fileName)
            const fileSizeBytes = att.fileBytes ? att.fileBytes.byteLength : 0
            attachmentObjects.push({
              fileName: att.fileName,
              fileSize: fileSizeBytes
            })
          }
        }
      }

      // Quote document attachments
      if (this.emailer && Array.isArray(this.emailer.quoteAttachments)) {
        for (const att of this.emailer.quoteAttachments) {
          const name = att && (att.fileName || att.filename)
          if (name && !processedNames.includes(name)) {
            processedNames.push(name)
            attachmentObjects.push({
              fileName: name,
              fileSize: att.originalFileSize || att.fileSize || 0
            })
          }
        }
      }

      // Selected images
      if (this.selectedImages && Array.isArray(this.selectedImages)) {
        for (const img of this.selectedImages) {
          const name = img && (img.fileName || img.filename)
          if (name && !processedNames.includes(name)) {
            processedNames.push(name)
            attachmentObjects.push({
              fileName: name,
              fileSize: img.originalFileSize || img.fileSize || 0
            })
          }
        }
      }

      return attachmentObjects
    }
  },
  watch: {
    $userInfo: function (newVal, oldVal) {
      if (oldVal === null) {
        this.resetEmailer()
        // this.loadReport()
      }
    }
    // 'emailer.attachments': {
    //   handler: function (newVal, oldVal) {
    //     if (this.selectedImages.length > 0) {
    //       const difference = this.selectedImages.filter(function (image) {
    //         const index = newVal.findIndex((i) => {
    //           if (typeof i === 'string') {
    //             return i === image.fileName
    //           } else if (i && i.fileName) {
    //             return i.fileName === image.fileName
    //           }
    //           return false
    //         })
    //         return index === -1
    //       })
    //       if (!_isEmpty(difference)) {
    //         const imageIndex = this.selectedImages.findIndex((i) => i.fileName === difference[0].fileName)
    //         this.selectedImages.splice(imageIndex, 1)
    //         const idIndex = this.selectedImageIds.findIndex((id) => id === difference[0].quoteImageId)
    //         this.selectedImageIds.splice(idIndex, 1, Guid.empty())
    //       }
    //     }
    //   },
    //   deep: true
    // }
    // selectedImages: {
    //   handler: function (newVal, oldVal) {
    //     this.updateAttachmentList()
    //   },
    //   deep: true
    // },
    // selectedDocuments: {
    //   handler: function (newVal, oldVal) {
    //     this.updateAttachmentList()
    //   },
    //   deep: true
    // }
  },
  created() {
    this.clearSessionStorage()
    this.checkCompanySetting()
  },
  mounted() {
    this.resetEmailer()
    this.loadReport()
  },
  methods: {
    async checkCompanySetting() {
      if (!this.company.setting) {
        await this.$store.dispatch('company/getCompanySetting')
      }
    },
    onHeaderButtonClicked(action) {
      switch (action) {
        case 'exportreport':
          this.showExportModal()
          break
        case 'email':
          this.email()
          break
        case 'print':
          this.print()
          break
        case 'back':
          // this.removeEmailer(this.$route.query.emailerId)
          this.$router.go(-1)
          break
      }
    },
    print() {},
    email() {
      this.isEmailModalActive = true
    },
    async exportReport(format) {
      this.$showSpinner()
      this.isExportModalActive = false
      await PrintPreviewService.exportReportEx(this.$route.params.reportName, format, this.parameters)
      this.exportFormat = ''
      this.$hideSpinner()
    },
    showExportModal(format) {
      this.isExportModalActive = true
    },
    closeExportModal() {
      this.isExportModalActive = false
    },
    async loadReport() {
      this.$showSpinner()
      this.resetReportParameters()
      // console.log(this.parameters)
      // this.cacheKey = await PrintPreviewService.cacheParameters(this.parameters)
      // console.log(this.cacheKey)
      // const result = await PrintPreviewService.getReportDataEx(this.$route.params.reportName, ReportFormatTypes.pdf, this.cacheKey)
      const result = await PrintPreviewService.postReportData(this.$route.params.reportName, ReportFormatTypes.pdf, this.parameters)
      this.reportContentType = result.headers['content-type'].toLowerCase()
      const pdfBlob = new Blob([result.data], { type: this.reportContentType})
      this.pdfUrl = URL.createObjectURL(pdfBlob)

      // Resize PDF viewer to fit
      const mainNav = document.getElementById('main-nav-menu')
      const appHeader = document.getElementById('app-detail-header')
      const pdfContainer = document.getElementById('pdf-container')
      this.toolbarHeight = mainNav.offsetHeight + appHeader.offsetHeight
      pdfContainer.style.height = window.innerHeight - mainNav.offsetHeight - appHeader.offsetHeight
      //this.updateAttachmentList()

      const updatedEmailer = _cloneDeep(this.emailer)
      if (!updatedEmailer.customAttachments) {
        this.$set(updatedEmailer, 'customAttachments', [])
      }
      // Add report file if not already present
      const reportFileName = `${updatedEmailer.reportName}.${this.reportExtension}`
      const reportFileSize = (result.data || []).byteLength || 0
      const existingReportIndex = updatedEmailer.customAttachments.findIndex((att) => att.fileName === reportFileName)
      if (existingReportIndex === -1) {
        updatedEmailer.customAttachments.push({
          fileName: reportFileName,
          fileSize: reportFileSize,
          fileBytes: result.data,
          fileType: this.reportContentType
        })
      }

      // Update the emailer reference
      this.emailer = updatedEmailer
      this.persistEmailer() // Persist changes to session storage

      this.$hideSpinner()
    },
    async sendEmail() {
      await this.$refs.emailer.send()
    },
    discardEmail() {
      this.isEmailModalActive = false
      this.isImageAttachActive = false
      this.isDocAttachActive = false
      this.selectedImageIds.splice(0, this.selectedImageIds.length)
      this.selectedImages.splice(0, this.selectedImages.length)
      this.resetEmailer()
    },
    attachMedia() {},
    async resetEmailer() {
      const sessionEmailer = this.retrieveEmailer() || StoreUtil.getLocalStorage(this.$route.query.emailerId, 'emailer')
      // console.log(sessionEmailer)
      if (sessionEmailer) {
        this.emailer = sessionEmailer
      } else {
        this.emailer = _cloneDeep(this.getEmailer(this.$route.query.emailerId) || new Emailer())
      }
      this.emailer.message = `\n\n${divider}\n${copyright.replace(/\[COMPANY\]/g, this.$userInfo.companyName)}`
      StoreUtil.removeLocalStorage(this.$route.query.emailerId, 'emailer')
      this.persistEmailer()
      if (this.isAllowAutoCC) {
        this.emailer.ccEmails.push(this.$userInfo.email)
      }

      this.resetAttachement()
      //this.updateAttachmentList()
    },
    resetAttachement() {
      const updatedEmailer = _cloneDeep(this.emailer)
      if (updatedEmailer.quoteImageAttachments) {
        this.$set(updatedEmailer, 'quoteImageAttachments', [])
      }
      if (updatedEmailer.attachments) {
        this.$set(updatedEmailer, 'attachments', [])
      }
      //reset for customAttachments
      if (updatedEmailer.customAttachments) {
        this.$set(updatedEmailer, 'customAttachments', [])
      }
      //reset for quoteAttachments
      if (updatedEmailer.quoteAttachments) {
        this.$set(updatedEmailer, 'quoteAttachments', [])
      }
      this.emailer = updatedEmailer
      this.persistEmailer()

      this.loadReport()

    },
    retrieveEmailer() {
      return JSON.parse(sessionStorage.getItem(`${this.$userInfo.sessionId}|${this.$route.meta.ekey}`))
    },
    persistEmailer() {
      sessionStorage.setItem(`${this.$userInfo.sessionId}|${this.$route.meta.ekey}`, JSON.stringify(this.emailer))
    },
    clearSessionStorage() {
      sessionStorage.removeItem(`${this.$userInfo.sessionId}|${this.$route.meta.ekey}`)
      sessionStorage.removeItem(`${this.$userInfo.sessionId}|${this.$route.meta.pkey}`)
    },
    async sent() {
      // 'sent' event from VueEmail component. Create email remark for asset e.g. QuickInvoice etc
      if (this.emailer.assetId && this.emailer.assetId !== Guid.empty()) {
        const remarkText = `${this.emailer.remark} has been emailed to ${this.emailer.toEmails.join(', ')}`
        const remark = {
          remarkId: Guid.empty(),
          assetId: this.emailer.assetId,
          remarks: remarkText,
          remarkTypeId: this.emailer.remarkTypeId
        }
        // console.log(remark)
        PrintPreviewService.postRemark(remark)

        if (`${this.emailer.subject}`.substring(0, 14) === 'Purchase Order') {
          let quoteId = await PrintPreviewService.getQuote(this.emailer.assetId)
          if (quoteId !== Guid.empty()) {
            const quoteRemark = {
              remarkId: Guid.empty(),
              assetId: quoteId,
              remarks: remarkText,
              remarkTypeId: this.emailer.remarkTypeId
            }
            PrintPreviewService.postRemark(quoteRemark)
          }
        }
      }
      this.discardEmail()
    },
    newEmailInput(newEmail) {
      this.isEnteringNewEmail = newEmail
    },
    handleValidationChanged(validation) {
      this.emailValidation = validation
    },
    showAttachImage() {
      this.isImageAttachActive = true
      this.isDocAttachActive = false
      // Initialize selectedImages with current quoteImageAttachments if any
      this.selectedImages = this.emailer.quoteImageAttachments ? [...this.emailer.quoteImageAttachments] : []
    },
    closeImageModal() {
      this.isImageAttachActive = false
      // Reset selections if canceled - restore from emailer
      this.selectedImages = this.emailer.quoteImageAttachments ? [...this.emailer.quoteImageAttachments] : []
    },
    confirmImageSelection() {
      // Create a deep clone of the emailer to avoid mutation issues
      const updatedEmailer = _cloneDeep(this.emailer)
      if (!updatedEmailer.quoteImageAttachments) {
        this.$set(updatedEmailer, 'quoteImageAttachments', [])
      }

      updatedEmailer.quoteImageAttachments = [...this.selectedImages]

      // Update the emailer reference
      this.emailer = updatedEmailer

      this.isImageAttachActive = false
      this.persistEmailer() // Persist changes to session storage
    },
    onImagesSelected(selectedImages) {
      this.selectedImages = selectedImages
    },
    closeDocModal() {
      this.isDocAttachActive = false
      // Reset selections if canceled - restore from emailer
      this.selectedDocuments = this.emailer.quoteAttachments ? [...this.emailer.quoteAttachments] : []
    },
    confirmDocSelection() {
      // Create a deep clone of the emailer to avoid mutation issues
      const updatedEmailer = _cloneDeep(this.emailer)
      if (!updatedEmailer.quoteAttachments) {
        this.$set(updatedEmailer, 'quoteAttachments', [])
      }
      updatedEmailer.quoteAttachments = [...this.selectedDocuments]

      // Update the emailer reference
      this.emailer = updatedEmailer

      this.isDocAttachActive = false
      this.persistEmailer() // Persist changes to session storage
    },
    onDocumentsSelected(docs) {
      this.selectedDocuments = Array.isArray(docs) ? docs : []
    },
    attachDocs() {
      this.isDocAttachActive = true
      this.isImageAttachActive = false
      // Initialize selectedDocuments with current quoteAttachments if any
      this.selectedDocuments = this.emailer.quoteAttachments ? [...this.emailer.quoteAttachments] : []
    },
    doneAttach() {
      // Close both image and document selectors when "Done" is clicked
      if (this.isImageAttachActive) {
        this.confirmImageSelection()
      }
      if (this.isDocAttachActive) {
        this.confirmDocSelection()
      }
      this.isImageAttachActive = false
      this.isDocAttachActive = false
    },
    updateAttachmentList(imagesForSend) {
      // Initialize attachments as array of objects with fileName and fileSize
      if (!this.emailer.attachments) {
        this.$set(this.emailer, 'attachments', [])
      } else {
        this.emailer.attachments.splice(0, this.emailer.attachments.length)
      }

      if (!this.emailer.customAttachments) {
        this.$set(this.emailer, 'customAttachments', [])
      }

      // Add report file if not already present
      const reportFileName = `${this.emailer.reportName}.${this.reportExtension}`
      const reportFileSize = this.reportBytes ? this.reportBytes.byteLength || this.reportBytes.length || 0 : 0
      const existingReportIndex = this.emailer.customAttachments.findIndex((att) => att.fileName === reportFileName)
      if (existingReportIndex === -1) {
        this.emailer.customAttachments.push({
          fileName: reportFileName,
          fileSize: reportFileSize
        })
      }

      if (!this.emailer.quoteImageAttachments) {
        this.$set(this.emailer, 'quoteImageAttachments', [])
      }
      // Add image attachments
      if (this.selectedImages && this.selectedImages.length) {
        for (const image of this.selectedImages) {
          if (image && image.fileName) {
            this.emailer.quoteImageAttachments.push({
              fileName: image.fileName,
              fileSize: image.originalFileSize || image.fileSize || 0
            })
          }
        }
      }

      //Add select document to quoteAttachments
      if (!this.emailer.quoteAttachments) {
        this.$set(this.emailer, 'quoteAttachments', [])
      }
      if (this.selectedDocuments && this.selectedDocuments.length) {
        for (const doc of this.selectedDocuments) {
          const fileName = doc && (doc.fileName || doc.filename || doc.name)
          if (fileName) {
            const existingIndex = this.emailer.quoteAttachments.findIndex((att) => att.fileName === fileName)
            if (existingIndex === -1) {
              this.emailer.quoteAttachments.push({
                fileName: fileName,
                fileSize: doc.originalFileSize || doc.fileSize || 0
              })
            }
          }
        }
      }
    },
    retrieveParameters() {
      const json = sessionStorage.getItem(`${this.$userInfo.sessionId}|${this.$route.meta.pkey}`)
      return json ? JSON.parse(json) : ''
    },
    async resetReportParameters() {
      const sessionParameters = this.retrieveParameters() || StoreUtil.getLocalStorage(this.$route.query.parameterId, this.$route.meta.pkey)
      // console.log('sessionParameters', sessionParameters)
      if (sessionParameters) {
        this.parameters = sessionParameters
      } else {
        this.parameters = _cloneDeep(this.reportParameters)
      }
      // console.log(this.parameters, this.reportParameters)
      StoreUtil.removeLocalStorage(this.$route.query.parameterId, this.$route.meta.pkey)
      this.persistParameters()
    },
    persistParameters() {
      sessionStorage.setItem(`${this.$userInfo.sessionId}|${this.$route.meta.pkey}`, JSON.stringify(this.parameters))
    },
    async onFileChange(event) {
      const files = Array.from(event.target.files)
      for (const file of files) {
        // Read file as ArrayBuffer (binary)
        const arrayBuffer = await file.arrayBuffer()
        const fileObj = {
          fileName: file.name,
          fileBytes: arrayBuffer,
          fileType: file.type
        }
        this.selectedImages.push(fileObj)
      }
      event.target.value = ''
    },
    async onAttachFiles(files) {
      // Convert FileList or array-like to array
      const fileArray = Array.from(files)

      if (!this.emailer.customAttachments) {
        this.$set(this.emailer, 'customAttachments', [])
      }

      const maxSizeMB = 25
      const maxSizeBytes = maxSizeMB * 1024 * 1024

      // Define allowed file types
      const allowedExtensions = [...this.allowedImageExtensions, ...this.allowedDocumentExtensions]

      const oversizedFiles = []
      const invalidTypeFiles = []
      const validFiles = []

      // Check file sizes and types
      for (const file of fileArray) {
        const fileName = file.name.toLowerCase()
        const fileExtension = fileName.substring(fileName.lastIndexOf('.'))

        // Check file type
        if (!allowedExtensions.includes(fileExtension)) {
          invalidTypeFiles.push(file.name)
          continue
        }

        // Check file size
        if (file.size > maxSizeBytes) {
          const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2)
          oversizedFiles.push(`${file.name} (${fileSizeMB}MB)`)
        } else {
          validFiles.push(file)
        }
      }

      // Show error for invalid file types
      if (invalidTypeFiles.length > 0) {
        const fileList = invalidTypeFiles.join(', ')
        const allowedTypes = 'Images (jpg, jpeg, png) and Documents (pdf)'
        this.$notification.error(
          'Invalid File Type',
          `The following files have unsupported file types and cannot be attached: ${fileList}. Only ${allowedTypes} are allowed.`
        )
      }

      // Show error for oversized files
      if (oversizedFiles.length > 0) {
        const fileList = oversizedFiles.join(', ')
        this.$notification.error('File Size Error', `The following files exceed the ${maxSizeMB}MB size limit and cannot be attached: ${fileList}`)
      }

      // Process only valid files
      for (const file of validFiles) {
        // Read file as ArrayBuffer (binary)
        const arrayBuffer = await file.arrayBuffer()
        const fileSizeMB = (arrayBuffer.byteLength / (1024 * 1024)).toFixed(2)
        const fileObj = {
          fileName: file.name,
          fileBytes: arrayBuffer,
          fileType: file.type,
          fileSize: arrayBuffer.byteLength,
          fileSizeMB: parseFloat(fileSizeMB)
        }
        // Add to customAttachments
        this.emailer.customAttachments.push(fileObj)
      }

      // this.updateAttachmentList()
      // console.log('onAttachFiles completed, customAttachments count:', this.emailer.customAttachments.length)
    },
    formatFileSize(sizeInBytes) {
      if (!sizeInBytes || sizeInBytes === 0) {
        return '0 KB'
      }

      const units = ['Bytes', 'KB', 'MB', 'GB']
      let size = sizeInBytes
      let unitIndex = 0

      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024
        unitIndex++
      }

      return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`
    }
  },
  beforeRouteLeave: function (to, from, next) {
    this.clearSessionStorage()
    this.removeEmailer(this.$route.query.emailerId)
    this.removeParameters(this.$route.query.parameterId)
    next()
  }
}
</script>

<style lang="scss" scoped>
object.pdf-document,
embeb.pdf-document {
  width: 100% !important;
  height: 100% !important;
}

.attachment-status {
  margin-top: 1rem;
  padding: 1rem;
  background-color: #f9f9f9;
  border-radius: 4px;
  border: 1px solid #e1e1e1;

  .file-size-text {
    color: #6b7280;
    font-size: 0.85em;
  }

  .progress-container {
    .progress {
      height: 8px;
      border-radius: 4px;

      &.is-danger::-webkit-progress-bar {
        background-color: #f14668;
      }

      &.is-success::-webkit-progress-bar {
        background-color: #48c774;
      }
    }
  }

  .tag.is-small.is-light {
    margin-right: 0.25rem;
    margin-bottom: 0.25rem;

    /* Dark theme support */
    --bulma-tag-background-color: var(--bulma-scheme-main-bis, #fafafa);
    --bulma-tag-color: var(--bulma-text, #4a4a4a);
    background-color: var(--bulma-tag-background-color);
    color: var(--bulma-tag-color);
  }

  /* Dark theme overrides */
  @media (prefers-color-scheme: dark) {
    background-color: #2f2f2f;
    border-color: #4a4a4a;

    .file-size-text {
      color: #9ca3af;
    }

    .tag.is-small.is-light {
      --bulma-tag-background-color: var(--bulma-scheme-main-ter, #363636);
      --bulma-tag-color: var(--bulma-text-invert, #ffffff);
    }
  }
}
</style>
